import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "First lessons learned about writing technical books",
  "description": "",
  "published": "2013-01-29T00:00:00.000Z",
  "redirect_from": ["/blog/first-lessons-learned-about-writing-technical-books/swizec/5912"],
  "categories": "HTML, New York Times, Publish, Technology, Uncategorized, Writers Resources",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A while ago a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Publishing",
        "title": "Publishing"
      }}>{`publisher`}</a>{` contacted me to write a book on d3.js.`}</p>
    <p>{`Even though I once managed to create a picture with d3.js and `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/quick-scatterplot-tutorial-for-d3-js/swizec/5337"
      }}>{`wrote a quick tutorial about it`}</a>{` on my blog, I didn't really know much about the library. It's a seriously confusing piece of technology that almost seems more black magic than common sense.`}</p>
    <p>{`Explains the loud clamouring for books on the topic. (two have been published so far)`}</p>
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2013/01/d3.js.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2013/01/d3.js.png",
          "alt": "d3.js",
          "title": "d3.js"
        }}></img></a></p>
    <p>{`After a few months of back and forth the contract was penned and signed, two weeks ago I finished the procrastination stage and started writing.`}</p>
    <p>{`I'm learning a lot writing this book, suddenly d3 is no longer a mystery only `}<a parentName="p" {...{
        "href": "https://twitter.com/mbostock"
      }}>{`mbostock`}</a>{` and `}<a parentName="p" {...{
        "href": "http://www.newyorktimes.com",
        "title": "New York Times"
      }}>{`New York Times`}</a>{` know how to use ... I'd go so far as saying the book will be better because I'm writing it `}<em parentName="p">{`as I learn`}</em>{` because I still remember which parts need more attention.`}</p>
    <p>{`This being my first technical book `}<em parentName="p">{`and`}</em>{` my first book with a real publisher and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/International_Standard_Book_Number",
        "title": "International Standard Book Number"
      }}>{`ISBN number`}</a>{`, I'm learning more than just d3.js.`}</p>
    <h2 {...{
      "id": "1-you-will-write-more-than-you-think"
    }}>{`1. You will write more than you think`}</h2>
    <p>{`Starting out, the publisher asked me to create an outline with a rough estimate of how many pages each chapter is going to be. I was supopsed to create a 100 page book and, to be honest, that seemed like an insurmountable task.`}</p>
    <p>{`A hundred pages! That's, dude, that's like a real book! I can't write a hundred pages about something I don't know, are you insane?`}</p>
    <p>{`But write an outline I did. I looked at each chapter, furrowed my eyebrow, twisted my lip into a grimace, and wrote down an imaginary number. This chapter will be 10 pages long! This one will be 30! Yes, that sounds about right.`}</p>
    <p>{`The number of pages evenly summed up to 100. Perfect!`}</p>
    <p>{`Then I started writing. The first chapter was going to be a 10 page quickstart into the world of d3.js, I would show you how to set up the environment for the rest of the book and explain three quick visualisations. Get something working within the first 2min of reading a book, y'know?`}</p>
    <p>{`It took me 10 pages to rush through a single example. Yes, `}<em parentName="p">{`rush`}</em>{` through. You get the depth of nothing, but you might be able to perform some inference learning and do stuff on your own.`}</p>
    <p>{`I am currently writing the second chapter, a 30 page deep explanation of the basics, at 36 pages I have covered about 70% of the material ... and that's including the buildup of knowledge that makes code explanations progressively less detailed.`}</p>
    <p>{`Code samples, their explanations and plenty of images since we're writing about, well, images builds up `}<em parentName="p">{`fast`}</em>{`.`}</p>
    <h2 {...{
      "id": "2-you-have-no-idea-how-much-time-you-need"
    }}>{`2. You have no idea how much time you need`}</h2>
    <p>{`Despite the insane amount of content you will create explaining basic concepts you previously thought were self-explanatory, writing a technical book is a stupidly slow process.`}</p>
    <p>{`Perhaps because I'm spending as much, if not more, time learning the material myself, playing around to see what things are doing and so on ... but it just takes `}<em parentName="p">{`time`}</em>{`. A lot of it.`}</p>
    <p>{`The publisher didn't pull their deadlines out of thin air. Well they did, but they asked me to approve them. `}<em parentName="p">{`Oh, what, I have until March to write this? And it's only December? Sure, that sounds perfectly reasonable!`}</em></p>
    <p>{`Then the first full writing weekend happened. When it was over I was the proud owner of slightly more than a first chapter. In fact, I had the whole first chapter and what I thought was a third of the second chapter.`}</p>
    <p>{`It had taken three days to write 20 pages.`}</p>
    <p>{`I was exhausted, I was dreaming `}<a parentName="p" {...{
        "href": "http://www.w3.org/Graphics/SVG/",
        "title": "Scalable Vector Graphics"
      }}>{`SVG`}</a>{` and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/HTML",
        "title": "HTML"
      }}>{`HTML`}</a>{`. It was terrible. Suddenly the deadline was super close. I could feel it nipping at my heels, smell its ugly breath as steam escaped its nostrils, I could almost feel its yellow teeth brushing against my thigh.`}</p>
    <p>{`Then you notice you forgot to explain something. But it doesn't fit at the end of the chapter because it would ruin the flow. You put it in the beginning and voila, just like that, you have to change some 20 screenshots.`}</p>
    <h2 {...{
      "id": "3-this-is-not-creative-writing-by-far"
    }}>{`3. This is not `}<a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Creative_writing",
        "title": "Creative writing"
      }}>{`creative writing`}</a>{`. By far.`}</h2>
    <p>{`The thing that really gets me most, though, is that writing a technical book is just not very creative. I know I know, a good writer can make any piece of writing fun and I certainly hope I'm making the book an entertaining read.`}</p>
    <p>{`Writing it, though, is just `}<em parentName="p">{`not fun`}</em>{`. It's `}<em parentName="p">{`work`}</em>{`.`}</p>
    <p>{`Schlep work through and through. You create an example. You have fun creating an example. You could spend countless hours just playing around, finding out how things work.`}</p>
    <p>{`Sometimes you do spend hours searching for a good example to illustrate your point without relying on the concepts you have yet to teach the reader. Then you avert your eyes to the other screen. Grab your keyboard. And `}<em parentName="p">{`write`}</em>{`.`}</p>
    <p>{`Explaining every little detail, making sure you didn't gloss over anything, keeping the reader engaged and interested ... it's ... it's just a little bit boring, you know? Sometimes I feel like an idiot for explaining something, like I'm insulting the reader by not assuming they know that crap.`}</p>
    <p>{`Then you show the writing to a friend who is a good representation of the target audience - `}<em parentName="p">{`Hey, can you explain that a bit more? You kind of glossed over it. I am surprised and confused. Your example is terrible.`}</em></p>
    <h2 {...{
      "id": "so-yeah-"
    }}>{`So yeah ...`}</h2>
    <p>{`Right now I'm writing two books at the same time. That was a `}<em parentName="p">{`terrible,`}</em>{` `}<strong parentName="p"><em parentName="strong">{`horrible`}</em></strong>{` idea, but here we are. I'm supposed to finish the d3.js book by March. I might have a chance at meeting that deadline.`}</p>
    <p>{`I'm afraid it is unlikely that the `}<em parentName="p">{`Why Programmers Work at Night`}</em>{` book should get an update in February. This makes me sad.`}</p>
    <p>{`But what makes me even more sad is that I am not entirely certain the publishing contract allows me to post this blogpost. I think it's okay because I didn't mention the publisher or the title. I am also uncertain whether I'm allowed to show the drafts to my friends, that's a bit of a grey area.`}</p>
    <p>{`Going to be showing the drafts to my friends anyway. Editor feedback is measured in months. A travesty really, feedback loops should be quick and efficient.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://juliecatherinevigna.wordpress.com/2013/01/28/raising-the-quality-bar-of-your-writing/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/141301257_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://juliecatherinevigna.wordpress.com/2013/01/28/raising-the-quality-bar-of-your-writing/"
        }}>{`Raising the "Quality Bar" of Your Writing`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.filipekberg.se/2012/08/27/self-publishing-a-book-part-1-where-it-all-began/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/141186200_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://blog.filipekberg.se/2012/08/27/self-publishing-a-book-part-1-where-it-all-began/"
        }}>{`I self-published a programming book, here's my story`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://onewildword.com/2013/01/28/a-killer-title-must-have-irony-and-tell-a-tale/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/141205738_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://onewildword.com/2013/01/28/a-killer-title-must-have-irony-and-tell-a-tale/"
        }}>{`A killer title must have irony and tell a tale`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://selfpubbooks.wordpress.com/2013/01/25/self-publishing-tips-book-writing-magic/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/140459854_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://selfpubbooks.wordpress.com/2013/01/25/self-publishing-tips-book-writing-magic/"
        }}>{`Self-Publishing Tips: Book Writing Magic`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://thebestchapter.com/2013/01/25/7-easy-rulesto-write-your-best-chapter-rule-1-write-your-worst-chapter/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/140554497_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://thebestchapter.com/2013/01/25/7-easy-rulesto-write-your-best-chapter-rule-1-write-your-worst-chapter/"
        }}>{`7 Easy RulesTo Write Your Best Chapter. (Rule 1: Write Your Worst Chapter.)`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=e5c8819e-f7af-4cd1-8948-31975e15d066",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      